import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import { decorateIntlMessages } from '../../common/rosetta_formatted_message';
import EpisodeBody from './episode_body';
import InternalVideoSection from '../../section/internal_video_section';
import FacebookVideoSection from '../../section/facebook_video_section';
import YoutubeSection from '../../section/youtube_section';
import { redirectToPath } from '../../../utils/url';
import { getSectionConstructor } from '../../../utils/sections';
import OpenerImagesPropTypes from '../../../utils/prop-types/OpenerImagesPropTypes';
import ResponsiveImg from '../../common/responsive_img';
import MediaQuery, { MEDIA_QUERIES } from '../../common/media_query';
import Spinner from '../../common/spinner';
import classNames from 'classnames';
import {filter, isEmpty, find, get} from 'lodash';
import { StaticAssets } from '../../common/static_assets.js';

const PREVIEW_IMG_NON_MOBILE_WIDTH = 1100;
const PREVIEW_IMG_NON_MOBILE_HEIGHT = PREVIEW_IMG_NON_MOBILE_WIDTH * 9 / 16;
const PREVIEW_IMG_MOBILE_WIDTH = MEDIA_QUERIES.mobile.maxWidth;
const PREVIEW_IMG_MOBILE_HEIGHT = PREVIEW_IMG_MOBILE_WIDTH * 9 / 16;
const PREVIEW_IMG_OPENER_TYPE = '16x9';

const PREVIEW_IMG_STATES = {
  // Preview image is shown, has no play button. Progress spinner is visible.
  loading: 0,
  // Preview image and both sub-elements are hidden.
  hidden: 1,
  // Preview image is shown, has a play button over it. Progress spinner is invisible.
  ready: 2
};

const i18nMessages = decorateIntlMessages(__filename, defineMessages({
  playButton: {
    id: 'play_button',
    description: 'Translation for play button alt text',
    defaultMessage: 'Play'
  }
}));

const Aux = props => props.children;

const previewOpenerImageSrc = props =>
  get(
    find(
      props.article.opener_images,
      { type: PREVIEW_IMG_OPENER_TYPE }
    ),
    'asset.src.full'
  );

export class VideoSeriesEntry extends Component {
  state = { previewImg: PREVIEW_IMG_STATES.loading };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (previewOpenerImageSrc(nextProps) !== previewOpenerImageSrc(this.props)) {
      this.setState({ previewImg: PREVIEW_IMG_STATES.loading });
    }

    if (getSectionConstructor(nextProps.videoSection) === YoutubeSection) {
      this.hidePreviewImageViaTimeout();
    }
  }

  videoType = () => getSectionConstructor(this.props.videoSection);

  redirectToNextEpisode = () =>
    !isEmpty(this.props.nextEpisode)
    && redirectToPath(this.props.nextEpisode.path);

  previewOpenerImage = () => {
    const openerImageSrc = previewOpenerImageSrc(this.props);
    if (!openerImageSrc) {
      return null;
    }

    return (
      <Aux>
        <MediaQuery breakpoint="mobile">
          <ResponsiveImg src={openerImageSrc}
            width={PREVIEW_IMG_MOBILE_WIDTH}
            height={PREVIEW_IMG_MOBILE_HEIGHT}
          />
        </MediaQuery>
        <MediaQuery breakpoint="notMobile">
          <ResponsiveImg src={openerImageSrc}
            width={PREVIEW_IMG_NON_MOBILE_WIDTH}
            height={PREVIEW_IMG_NON_MOBILE_HEIGHT}
          />
        </MediaQuery>
      </Aux>
    );
  };

  textOverlay = () => {
    const { show, episode, meta_description } = this.props;
    return (
      <MediaQuery breakpoint="desktop">
        <div className="text-overlay">
          <div className="show-title"
            dangerouslySetInnerHTML={{ __html: show.display_name }}
          />
          <span className="episode-title"
            dangerouslySetInnerHTML={{ __html: episode.title }}
          />
          <p className="description"
            dangerouslySetInnerHTML={{ __html: meta_description }}
          />
        </div>
      </MediaQuery>
    );
  };

  showPlayButton = () => {
    if (this.state.previewImg === PREVIEW_IMG_STATES.loading) {
      this.setState({ previewImg: PREVIEW_IMG_STATES.ready });
    }
  }

  hidePreviewImage = () => {
    if (this.state.previewImg !== PREVIEW_IMG_STATES.hidden) {
      this.setState({ previewImg: PREVIEW_IMG_STATES.hidden });
    }
  }

  /*
   * We should be careful that when `props.timeoutHandler` fires,
   * we _don't_ hide a preview image that is in the 'ready' state.
   */
  hidePreviewImageViaTimeout = () => {
    if (this.state.previewImg === PREVIEW_IMG_STATES.loading) {
      this.setState({ previewImg: PREVIEW_IMG_STATES.hidden });
    }
  }

  playButtonOrSpinner() {
    if (this.videoType() === YoutubeSection) {
      return null;
    }

    switch (this.state.previewImg) {
      case PREVIEW_IMG_STATES.loading:
        return (
          <Aux>
            <div className="white-circle-underlay" />
            <Spinner inline />
          </Aux>
        );
      case PREVIEW_IMG_STATES.ready:
        return (
          <Aux>
            <div className="white-circle-underlay" />
            <img
              src={StaticAssets.videoCircleDarkArrow}
              className="play-button"
              alt={this.props.intl.formatMessage(i18nMessages.playButton)}
            />
          </Aux>
        );
      case PREVIEW_IMG_STATES.hidden:
        return null;
    }
  }

  render() {
    const {
      adsState, article, show, episode,
      spotID, videoSection, textSections,
      meta_title, meta_description
    } = this.props;

    let videoSectionComponent;
    switch (this.videoType()) {
      case InternalVideoSection:
        videoSectionComponent = (
          <InternalVideoSection
            {...videoSection}

            readyHandler={this.showPlayButton}
            timeoutHandler={this.hidePreviewImageViaTimeout}
            clickHandler={this.hidePreviewImage}
            /*
             * Why is `playHandler` here?
             *
             * In case it's an autoplaying JW video, we need to make sure we close
             * the preview image automatically when the video starts playing.
             * Note that on non-autoplaying JW videos, this function still gets called...
             * it just doesn't do anything, since the user has already dismissed
             * the preview image.
             *
             * As of April 2018, we're configured to NEVER autoplay on JW videos,
             * but we want to still support it just in case something breaks.
             */
            playHandler={this.hidePreviewImage}
            completionHandler={this.redirectToNextEpisode}

            adsState={adsState}
            sold_content_type={article.meta.sold_content_type}
            url_full={article.url_full}
            sanitizedTitle={article.sanitizedTitle}
            isFirstSection
            shouldHideContentCaption
          />
        );
        break;
      case YoutubeSection:
        videoSectionComponent = (
          <YoutubeSection
            {...videoSection}

            completionHandler={this.redirectToNextEpisode}

            isFirstSection
            shouldHideContentCaption
            shouldHideInfo // disables the video title, etc at the top of the player,
            /*
             * so that a user can't click on those elements
             * while they are underneath the preview image,
             * inadvertedly opening a new tab
             */
            shouldAutoplay
            noRel
          />
        );
        break;
      case FacebookVideoSection:
        videoSectionComponent = (
          <FacebookVideoSection
            {...videoSection}

            readyHandler={this.showPlayButton}
            timeoutHandler={this.hidePreviewImageViaTimeout}
            clickHandler={this.hidePreviewImage}
            completionHandler={this.redirectToNextEpisode}

            shouldHideContentCaption
          />
        );
        break;
      default:
        break;
    }

    const previewClassName = classNames('video-series-preview-image', filter([
      'hidden',
      'ready'
    ], state => this.state.previewImg === PREVIEW_IMG_STATES[state]));

    return (
      <div className="video-series-entry">
        <div className={previewClassName}>
          {this.previewOpenerImage()}
          {this.textOverlay()}
          {this.playButtonOrSpinner()}
        </div>
        {videoSectionComponent}
        <EpisodeBody
          article={article}
          episode={episode}
          meta_title={meta_title}
          meta_description={meta_description}
          show={show}
          spotID={spotID}
          textSections={textSections}
        />
      </div>
    );
  }
}

VideoSeriesEntry.propTypes = {
  adsState: PropTypes.object.isRequired,
  article: PropTypes.shape({
    meta: PropTypes.shape({
      sold_content_type: PropTypes.string
    }).isRequired,
    opener_images: OpenerImagesPropTypes.isRequired
  }).isRequired,
  episode: PropTypes.shape({
    title: PropTypes.string.isRequired
  }).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }),
  meta_title: PropTypes.string.isRequired,
  meta_description: PropTypes.string.isRequired,
  nextEpisode: PropTypes.object.isRequired,
  show: PropTypes.shape({
    display_name: PropTypes.string.isRequired
  }).isRequired,
  season: PropTypes.shape({
    opener_images: OpenerImagesPropTypes.isRequired
  }),
  spotID: PropTypes.string.isRequired,
  videoSection: PropTypes.shape({
    type: PropTypes.string.isRequired,
    content: PropTypes.object.isRequired
  }).isRequired,
  textSections: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default injectIntl(VideoSeriesEntry);
